// ============================================================================
// Règles déclaratives — schéma `RuleSpec` v1 (lot 1 de la refonte).
//
// Une règle est une DONNÉE : des formes mathématiques lues par notre parseur,
// des préconditions et des effets pris dans un vocabulaire FERMÉ. Aucun code,
// aucune expression JavaScript, aucun import : toute clé inconnue est refusée
// (zod `.strict()` à chaque niveau), et le texte des formes passe par une liste
// blanche de caractères et de mots (cf. forms.ts).
//
// Écriture JSON (snake_case, comme les règles de carte existantes) :
//
//   {
//     "id": "algebra.difference_of_squares", "version": 1, "kind": "identity",
//     "vars": { "a": "real", "b": "real" },
//     "forms": { "diff": "?a² − ?b²", "prod": "(?a − ?b)(?a + ?b)" },
//     "directions": [{ "from": "diff", "to": "prod", "goals": ["factor"] }, …],
//     "label": "a² − b² = (a − b)(a + b)",
//     "examples": [{ "from": "x² − 9", "to": "(x − 3)(x + 3)", "expect": "valid" }]
//   }
//
// La garantie (identité, équivalence, implication) découle de `kind` : un seul
// champ, pas deux qui pourraient se contredire.
// ============================================================================

import { z } from 'zod';
import { GoalKindSchema, RULE_ID_RE, SortSchema, type StepRelation } from '../types';

export const RULE_KINDS = ['identity', 'equivalence', 'implication'] as const;
export type RuleKind = (typeof RULE_KINDS)[number];

/** Relation garantie par chaque genre de règle. */
export const RULE_KIND_RELATION: Record<RuleKind, StepRelation> = {
  identity: 'identity',
  equivalence: 'equivalent',
  implication: 'implies',
};

/** Sur quoi porte la règle : une expression (identité), une équation, une inéquation. */
export const RULE_TARGETS = ['expression', 'equation', 'inequality'] as const;
export type RuleTarget = (typeof RULE_TARGETS)[number];

/** Préconditions sur les variables de motif (sous-ensemble fermé des conditions du noyau). */
export const PRECONDITION_KINDS = [
  'real',
  'integer',
  'natural',
  'nonzero',
  'positive',
  'negative',
  'nonnegative',
  'distinct',
] as const;
export type PreconditionKind = (typeof PRECONDITION_KINDS)[number];

/** Que faire quand une précondition n'est pas connue : refuser, brancher, ou conclure sous condition. */
export const ON_UNKNOWN = ['refuse', 'branch', 'conditional'] as const;
export type OnUnknown = (typeof ON_UNKNOWN)[number];

/** Niveaux scolaires (miroir des valeurs de `school_level`, @jeraisonne/db). */
export const RULE_LEVELS = [
  '6e',
  '5e',
  '4e',
  '3e',
  '2nde',
  '1re',
  'tle',
  'mpsi',
  'ptsi',
  'mp',
  'pt',
] as const;

/** Nom d'une variable de motif : une lettre, un indice facultatif (« a », « u₁ »). */
export const PATTERN_VAR_RE = /^[A-Za-zα-ω][₀-₉]?$/;
/** Référence à une variable de motif dans une précondition : « ?k ». */
export const PATTERN_REF_RE = /^\?[A-Za-zα-ω][₀-₉]?$/;
/** Variable libre d'un exemple (« x », « AB », « u₁ »). */
export const EXAMPLE_VAR_RE = /^[A-Za-zα-ω]{1,2}[₀-₉]?$/;

const PACK_ID_RE = /^[a-z][a-z0-9-]{1,39}$/;
const FORM_KEY_RE = /^[a-z][a-z0-9_]{0,23}$/;
const TAG_RE = /^[a-z][a-z0-9-]{0,31}$/;

const FormTextSchema = z.string().trim().min(1).max(200);
const ExampleTextSchema = z.string().trim().min(1).max(200);

/** `{ "negative": "?k" }`, `{ "distinct": ["?a", "?b"] }` : une seule clé par précondition. */
function conditionRecord(ref: RegExp) {
  return z
    .record(
      z.enum(PRECONDITION_KINDS),
      z.union([z.string().regex(ref), z.array(z.string().regex(ref))]),
    )
    .superRefine((rec, ctx) => {
      const keys = Object.keys(rec) as PreconditionKind[];
      if (keys.length !== 1) {
        ctx.addIssue({ code: 'custom', message: 'une condition = une seule clé' });
        return;
      }
      const k = keys[0]!;
      const v = rec[k];
      if (k === 'distinct') {
        if (!Array.isArray(v) || v.length < 2 || v.length > 4)
          ctx.addIssue({ code: 'custom', path: [k], message: '2 à 4 termes distincts' });
      } else if (typeof v !== 'string') {
        ctx.addIssue({ code: 'custom', path: [k], message: 'un seul terme' });
      }
    });
}

export const PreconditionSchema = conditionRecord(PATTERN_REF_RE);
export const ExampleAssumeSchema = conditionRecord(EXAMPLE_VAR_RE);

export const EffectSchema = z.object({ flip_relation: z.literal(true) }).strict();

export const DirectionSchema = z
  .object({
    from: z.string().regex(FORM_KEY_RE),
    to: z.string().regex(FORM_KEY_RE),
    goals: z.array(GoalKindSchema).min(1).max(8),
  })
  .strict();

export const RuleExampleSchema = z
  .object({
    from: ExampleTextSchema,
    to: ExampleTextSchema,
    expect: z.enum(['valid', 'invalid']),
    assume: z.array(ExampleAssumeSchema).max(8).optional(),
  })
  .strict();

export const RuleSpecSchema = z
  .object({
    id: z.string().regex(RULE_ID_RE),
    version: z.number().int().min(1).max(999),
    kind: z.enum(RULE_KINDS),
    applies_to: z.enum(RULE_TARGETS).optional(),
    vars: z
      .record(z.string().regex(PATTERN_VAR_RE), SortSchema)
      .refine((v) => Object.keys(v).length >= 1 && Object.keys(v).length <= 8, '1 à 8 variables'),
    forms: z
      .record(z.string().regex(FORM_KEY_RE), FormTextSchema)
      .refine((f) => Object.keys(f).length >= 2 && Object.keys(f).length <= 6, '2 à 6 formes'),
    directions: z.array(DirectionSchema).min(1).max(8),
    preconditions: z.array(PreconditionSchema).max(8).optional(),
    on_unknown: z.enum(ON_UNKNOWN).optional(),
    effects: z.array(EffectSchema).max(4).optional(),
    cost: z.number().int().min(0).max(100).optional(),
    label: z.string().trim().min(1).max(120),
    /** propriété invoquée (« identité remarquable », « règle du produit nul ») */
    ref: z.string().trim().min(1).max(200).optional(),
    tags: z.array(z.string().regex(TAG_RE)).max(8).optional(),
    levels: z.array(z.enum(RULE_LEVELS)).max(11).optional(),
    limits: z
      .object({ max_matches: z.number().int().min(1).max(1000) })
      .strict()
      .optional(),
    /** rejoués au chargement : un exemple qui échoue fait refuser tout le paquet */
    examples: z.array(RuleExampleSchema).min(1).max(16),
  })
  .strict();
export type RuleSpec = z.infer<typeof RuleSpecSchema>;

export const RULE_PACK_SCHEMA_VERSION = 1 as const;

export const RulePackSchema = z
  .object({
    id: z.string().regex(PACK_ID_RE),
    version: z.number().int().min(1).max(999),
    schema: z.literal(RULE_PACK_SCHEMA_VERSION),
    label: z.string().trim().min(1).max(120).optional(),
    rules: z.array(RuleSpecSchema).min(1).max(500),
  })
  .strict();
export type RulePackSpec = z.infer<typeof RulePackSchema>;
